<template>
    <div role="region" class="accordion-tab" ref="accordionTabRef">
        <div
            class="accordion-tab__header"
            :class="{
                'accordion-tab__header--expanded': isTabOpen ?? notAccordion,
            }"
            role="button"
            ref="accordionTabHeaderRef"
            v-if="!hideHeader"
            :aria-expanded="isTabOpen"
            @click="headerClick"
        >
            <div class="accordion-tab__headline-wrapper" v-if="$slots.headline ?? $slots.icon">
                <slot name="icon" />
                <div class="accordion-tab__headline" v-if="$slots.headline">
                    <slot name="headline" />
                    <slot name="icon-after" />
                </div>
            </div>
            <slot name="headlineAfter" v-if="$slots.headlineAfter && isTabOpen" />
            <div class="accordion-tab__markets" v-if="$slots.markets">
                <slot name="markets" />
            </div>
            <div class="accordion-tab__controls" v-if="$slots.controls">
                <slot name="controls"></slot>
            </div>
            <slot name="icon-toggle">
                <SvgIcon
                    v-if="!hideChevron && !notAccordion"
                    name="dropdown-down"
                    class="accordion-tab__icon"
                    :class="{ 'accordion-tab__icon--rotate': isTabOpen }"
                    :size="chevronSize"
                />
                <!-- We still need the space to not shift the oddsHeaders layout-->
                <span v-else class="accordion-tab__icon--placeholder"></span>
            </slot>
        </div>
        <slot name="chips"></slot>
        <div class="accordion-tab__fake-header" v-if="$slots.secondHeadline && isDesktop">
            <slot name="secondHeadline" />
        </div>
        <div
            :class="{ 'tab-closed': !isTabOpen }"
            class="accordion-tab__content"
            ref="accordionTabContentRef"
            v-show="isTabOpen"
        >
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, inject, computed, onMounted, watch, useSlots, VNodeChild, Ref } from "vue";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import analytics from "@/shared/analytics";
import useUtils from "@/shared/composables/use-utils";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";

const { extractTextFromVNode } = useUtils();
const { isDesktop } = useIsBreakpoint();
const slots = useSlots();

const emit = defineEmits(["on-open", "on-close", "on-open-click", "on-close-click", "on-tab-mounted"]);
const props = withDefaults(
    defineProps<{
        hideHeader?: boolean;
        hideChevron?: boolean;
        forceOpen?: boolean;
        open?: boolean;
        analyticsPlacement?: "main" | "subMenu" | "miniticket";
        chevronSize?: number;
        disableAnalytics?: boolean;
        notAccordion?: boolean;
    }>(),
    {
        open: true,
        analyticsPlacement: "main",
        chevronSize: 20,
    },
);

const openTabs = inject("openTabs") as Ref<number[]>;
const toggleTab = inject("toggleTab") as (index: number) => void;

const accordionTabRef = ref<HTMLDivElement | null>(null);
const isTabOpen = ref(props.open);
const index = ref<number>(-1);
const silent = ref(false);

onMounted(() => {
    index.value = Array.from(accordionTabRef.value?.parentElement?.children ?? []).indexOf(accordionTabRef.value);
    emit("on-tab-mounted");
    if (props.open) {
        openTabs.value.push(index.value);
    } else {
        openTabs.value = openTabs.value.filter((i) => i !== index.value);
    }
});

const computedIsTabOpen = computed(() => openTabs.value.includes(index.value) ?? props.open);

watch(computedIsTabOpen, (newVal) => {
    isTabOpen.value = newVal;
});

const headerClick = () => {
    if (props.notAccordion) return;
    toggleTab(index.value);
    setTimeout(() => {
        emit(isTabOpen.value ? "on-open-click" : "on-close-click");
    });
};

function silentToggle(open: boolean) {
    silent.value = true;

    setTimeout(() => {
        isTabOpen.value = open;
        silent.value = false;
    }, 0);
}

watch(
    () => props.forceOpen,
    (newVal) => {
        if (isTabOpen.value === newVal) return;
        silentToggle(newVal);
    },
);

watch(isTabOpen, (newValue: boolean) => {
    if (silent.value) return;
    let headlineContent = slots.headline ? extractTextFromVNode(slots.headline({})[0] as VNodeChild) : "";
    if (!headlineContent) {
        if (!slots.headline) {
            headlineContent = "";
        } else {
            headlineContent = slots.headline({})[0]?.props.text ?? "";
        }
    }
    if (newValue) {
        emit("on-open");
        if (!props.disableAnalytics) {
            analytics.sendExpandingDetail({
                eventProperty1: props.analyticsPlacement,
                eventProperty2: headlineContent,
            });
        }
    } else {
        emit("on-close");
        if (!props.disableAnalytics) {
            analytics.sendCollapsingDetail({
                eventProperty1: props.analyticsPlacement,
                eventProperty2: headlineContent,
            });
        }
    }
});
</script>
